/**
 * Plan for moving one Files structure group onto another project.
 * Pure: the service applies the plan. Whole-project tracks stay behind.
 */

import type { ProjectFile, ProjectFileStream, ProposalNodeSet } from '../types';
import { groupKeyForStream, WHOLE_PROJECT_GROUP_ID } from './projectFileStreamGroups';

export interface StreamMoveUpdate {
  streamId: string;
  structureIds: string[];
  structureNames: string[];
  /** Parent track stays on the source project, so this track stops inheriting it. */
  clearForkedFrom: boolean;
}

export interface ParentForkUpdate {
  streamId: string;
  /** Remaining children. Empty means the field should be deleted. */
  forkedToStreamIds: string[];
}

export interface FileMoveUpdate {
  fileId: string;
  /** Present only when a colliding structure id was rewritten on this file. */
  cncStructureIds?: string[];
  cncStructureNames?: string[];
}

export interface MoveStructureGroupPlan {
  structureIds: string[];
  /** Old structure id → id to use on the destination. Only collisions are listed. */
  idRemap: Record<string, string>;
  movedNodeSets: ProposalNodeSet[];
  sourceNodeSets: ProposalNodeSet[];
  destinationNodeSets: ProposalNodeSet[];
  streamUpdates: StreamMoveUpdate[];
  parentForkUpdates: ParentForkUpdate[];
  fileUpdates: FileMoveUpdate[];
}

export interface MoveStructureGroupInput {
  groupKey: string;
  streams: ProjectFileStream[];
  files: ProjectFile[];
  sourceNodeSets: ProposalNodeSet[];
  destinationNodeSets: ProposalNodeSet[];
  /** Used when the destination proposal already has this structure id. */
  nextStructureId?: (oldId: string, taken: Set<string>) => string;
}

export function defaultNextStructureId(oldId: string, taken: Set<string>): string {
  let n = 0;
  let id = '';
  do {
    id = `nodeset-${oldId}-${n}`;
    n += 1;
  } while (taken.has(id));
  return id;
}

function withoutPhase(nodeSet: ProposalNodeSet, id: string): ProposalNodeSet {
  const next: ProposalNodeSet = { ...nodeSet, id };
  delete next.phaseId;
  return next;
}

/**
 * Which tracks, files, and proposal structures leave with this rail group.
 * Throws when the group is Whole project or has nothing to move.
 */
export function planMoveStructureGroup(input: MoveStructureGroupInput): MoveStructureGroupPlan {
  const groupKey = input.groupKey.trim();
  if (!groupKey || groupKey === WHOLE_PROJECT_GROUP_ID) {
    throw new Error('Whole project cannot be moved to another project.');
  }

  const grouped = input.streams.filter(stream => stream.id && groupKeyForStream(stream) === groupKey);
  const movingStreams = grouped.filter(stream => !stream.wholeProjectRoot);
  const movingStreamIds = new Set(movingStreams.map(stream => stream.id!));

  const structureIds: string[] = [];
  const seenStructureIds = new Set<string>();
  for (const stream of grouped) {
    for (const id of stream.structureIds ?? []) {
      const trimmed = (id || '').trim();
      if (!trimmed || seenStructureIds.has(trimmed)) continue;
      seenStructureIds.add(trimmed);
      structureIds.push(trimmed);
    }
  }

  const movingNodeSets = input.sourceNodeSets.filter(nodeSet => seenStructureIds.has(nodeSet.id));
  if (movingStreams.length === 0 && movingNodeSets.length === 0) {
    throw new Error('This group has nothing to move.');
  }

  const nextId = input.nextStructureId ?? defaultNextStructureId;
  const taken = new Set(input.destinationNodeSets.map(nodeSet => nodeSet.id));
  const idRemap: Record<string, string> = {};
  for (const nodeSet of movingNodeSets) {
    if (!taken.has(nodeSet.id)) {
      taken.add(nodeSet.id);
      continue;
    }
    const id = nextId(nodeSet.id, taken);
    if (!id || id === nodeSet.id || taken.has(id)) {
      throw new Error(`Could not assign a new id for structure ${nodeSet.name || nodeSet.id}.`);
    }
    idRemap[nodeSet.id] = id;
    taken.add(id);
  }

  const nameByOldId = new Map(input.sourceNodeSets.map(nodeSet => [nodeSet.id, nodeSet.name]));
  const movedNodeSets = movingNodeSets.map(nodeSet => withoutPhase(nodeSet, idRemap[nodeSet.id] ?? nodeSet.id));
  const movingIds = new Set(movingNodeSets.map(nodeSet => nodeSet.id));
  const sourceNodeSets = input.sourceNodeSets.filter(nodeSet => !movingIds.has(nodeSet.id));
  const destinationNodeSets = [...input.destinationNodeSets, ...movedNodeSets];

  const mapId = (id: string) => idRemap[id] ?? id;

  const streamUpdates: StreamMoveUpdate[] = movingStreams.map(stream => {
    const oldIds = (stream.structureIds ?? []).filter(Boolean);
    return {
      streamId: stream.id!,
      structureIds: oldIds.map(mapId),
      structureNames: oldIds.map((id, index) => nameByOldId.get(id) || stream.structureNames?.[index] || 'Structure'),
      clearForkedFrom: Boolean(stream.forkedFromStreamId && !movingStreamIds.has(stream.forkedFromStreamId)),
    };
  });

  const detached = new Set(streamUpdates.filter(update => update.clearForkedFrom).map(update => update.streamId));
  const parentForkUpdates: ParentForkUpdate[] = [];
  for (const stream of input.streams) {
    if (!stream.id || !stream.forkedToStreamIds?.length) continue;
    const next = stream.forkedToStreamIds.filter(id => !detached.has(id));
    if (next.length !== stream.forkedToStreamIds.length) {
      parentForkUpdates.push({ streamId: stream.id, forkedToStreamIds: next });
    }
  }

  const fileUpdates: FileMoveUpdate[] = [];
  for (const file of input.files) {
    if (!file.id || !file.streamId || !movingStreamIds.has(file.streamId)) continue;
    if (file.fileSection === 'quote') continue;
    const cncIds = file.cncStructureIds ?? [];
    const hitsRemap = cncIds.some(id => idRemap[id]);
    const update: FileMoveUpdate = { fileId: file.id };
    if (hitsRemap) {
      update.cncStructureIds = cncIds.map(mapId);
      update.cncStructureNames = cncIds.map((id, index) => nameByOldId.get(id) || file.cncStructureNames?.[index] || '');
    }
    fileUpdates.push(update);
  }

  return {
    structureIds,
    idRemap,
    movedNodeSets,
    sourceNodeSets,
    destinationNodeSets,
    streamUpdates,
    parentForkUpdates,
    fileUpdates,
  };
}
